import { useEffect, useState } from "react";
import api from "../../api/api";
import { toast } from "react-toastify";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

const SEZ = [
  { key: "payroll_zucchetti",  label: "Payroll Zucchetti", color: "#3b82f6" },
  { key: "payroll_iipay",      label: "Payroll iiPay",     color: "#8b5cf6" },
  { key: "altri_adempimenti",  label: "Altri adem.",       color: "#f59e0b" },
  { key: "forfait",            label: "Forfait",           color: "#10b981" },
];

const fmt = (v) =>
  v != null
    ? Number(v).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : "0";

const fmtK = (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v));

const meseLabel = (d) => `${MESI[d.mese - 1]} ${d.anno}`;

const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const tot = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded shadow-md p-2 text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill }}>
          {p.name}: €{fmt(p.value)}
        </p>
      ))}
      <p className="font-semibold mt-1 border-t pt-1">Totale: €{fmt(tot)}</p>
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white border border-gray-200 rounded shadow-md p-2 text-xs">
      <p style={{ color: p.payload.color }} className="font-semibold">{p.name}</p>
      <p>€{fmt(p.value)} ({(p.payload.pct * 100).toFixed(1)}%)</p>
    </div>
  );
};

export const BillingDashboard = () => {
  const [dati, setDati]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [selIdx, setSelIdx]   = useState(0);

  useEffect(() => {
    api.get("/billing/dashboard")
      .then((r) => {
        const rows = r.data || [];
        const oggi = new Date();
        const currentIdx = rows.findIndex((row) => (
          Number(row.anno) === oggi.getFullYear() && Number(row.mese) === oggi.getMonth() + 1
        ));
        setDati(rows);
        setSelIdx(currentIdx >= 0 ? currentIdx : 0);
      })
      .catch(() => toast.error("Errore caricamento dati fatturazione"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="col-span-2 el-container">
      <p className="text-sm text-gray-400 p-4">Caricamento dati fatturazione...</p>
    </div>
  );
  if (!dati.length) return (
    <div className="col-span-2 el-container">
      <p className="text-sm text-gray-400 p-4">Nessun dato di fatturazione disponibile.</p>
    </div>
  );

  const sel = dati[selIdx];
  const totSel = Number(sel.totale);

  // donut: sezioni del periodo selezionato
  const pieData = SEZ.map((s) => ({
    name:  s.label,
    value: Number(sel[s.key]),
    color: s.color,
    pct:   totSel > 0 ? Number(sel[s.key]) / totSel : 0,
  })).filter((d) => d.value > 0);

  // barchart: tutti i periodi in ordine cronologico, evidenzia quello selezionato
  const barData = [...dati].reverse().map((d, i) => ({
    name:               meseLabel(d),
    payroll_zucchetti:  Number(d.payroll_zucchetti),
    payroll_iipay:      Number(d.payroll_iipay),
    altri_adempimenti:  Number(d.altri_adempimenti),
    forfait:            Number(d.forfait),
    _idx:               dati.length - 1 - i,
  }));

  return (
    <div className="col-span-2">
      <div className="el-container">

        {/* header con selettore periodo */}
        <div className="flex items-center justify-between px-3 pt-2 pb-2 border-b border-gray-100 mb-3">
          <h4 className="text-sm font-semibold text-gray-700">Fatturazione</h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Periodo:</span>
            <select
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
              value={selIdx}
              onChange={(e) => setSelIdx(Number(e.target.value))}
            >
              {dati.map((d, i) => (
                <option key={i} value={i}>{meseLabel(d)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-3 gap-3 px-3 mb-4">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-xs text-blue-500 font-medium uppercase tracking-wide mb-1">
              Totale {meseLabel(sel)}
            </p>
            <p className="text-2xl font-bold text-blue-700">€{fmt(sel.totale)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-xs text-green-500 font-medium uppercase tracking-wide mb-1">Clienti fatturabili</p>
            <p className="text-2xl font-bold text-green-700">{sel.n_clienti}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Stato righe</p>
            <div className="flex justify-center gap-3 mt-1">
              <span className="text-xs"><span className="font-bold text-gray-600">{sel.n_bozza}</span> <span className="text-gray-400">bozza</span></span>
              <span className="text-xs"><span className="font-bold text-green-600">{sel.n_validata}</span> <span className="text-gray-400">validata</span></span>
              <span className="text-xs"><span className="font-bold text-blue-600">{sel.n_chiusa}</span> <span className="text-gray-400">chiusa</span></span>
            </div>
          </div>
        </div>

        {/* charts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-3 pb-4">

          {/* barchart — 2/3 */}
          <div className="md:col-span-2">
            <p className="text-xs font-medium text-gray-500 mb-2">Andamento mensile per sezione</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barData} barSize={28} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} width={42} />
                <Tooltip content={<BarTooltip />} />
                {SEZ.map((s) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    stackId="a"
                    fill={s.color}
                    radius={s.key === "forfait" ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                  >
                    {barData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={s.color}
                        opacity={entry._idx === selIdx ? 1 : 0.45}
                      />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
            {/* legenda bar */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 justify-center">
              {SEZ.map((s) => (
                <span key={s.key} className="flex items-center gap-1 text-xs text-gray-500">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
          </div>

          {/* donut — 1/3 */}
          <div className="flex flex-col">
            <p className="text-xs font-medium text-gray-500 mb-2">Ripartizione {meseLabel(sel)}</p>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={82}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={2}
                  label={false}
                  labelLine={false}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* legenda donut esterna */}
            <div className="flex flex-col gap-1.5 mt-2">
              {pieData.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                    <span className="text-gray-600">{d.name}</span>
                  </span>
                  <span className="font-medium text-gray-700 ml-2">
                    {(d.pct * 100).toFixed(0)}% <span className="text-gray-400 font-normal">€{fmt(d.value)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
